import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, FolderOpen } from 'lucide-react';
import { useStudio } from '@/lib/store';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GalleryDrawer({ open, onOpenChange }) {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadDesign = useStudio((s) => s.loadDesign);

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/designs`);
      setDesigns(data);
    } catch {
      toast.error('Could not load gallery');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchList();
  }, [open]);

  const handleLoad = async (id) => {
    try {
      const { data } = await axios.get(`${API}/designs/${id}`);
      loadDesign(data);
      toast.success(`Loaded "${data.name}"`);
      onOpenChange(false);
    } catch {
      toast.error('Load failed');
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/designs/${id}`);
      setDesigns((d) => d.filter((x) => x.id !== id));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col" data-testid="gallery-drawer">
        <SheetHeader>
          <SheetTitle className="tracking-tight">Saved Designs</SheetTitle>
          <SheetDescription>Load a previous design back into the studio.</SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-3 overflow-y-auto pr-1 flex-1 min-h-0" data-testid="gallery-list">
          {loading && <div className="text-sm text-slate-500">Loading…</div>}
          {!loading && designs.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center">
              <FolderOpen className="w-8 h-8 text-slate-400 mx-auto mb-3" />
              <div className="text-sm text-slate-600 font-medium">No saved designs yet</div>
              <div className="text-xs text-slate-400 mt-1">Design something, then hit Export → Save to Gallery.</div>
            </div>
          )}
          {designs.map((d) => (
            <button
              key={d.id}
              onClick={() => handleLoad(d.id)}
              data-testid={`gallery-card-${d.id}`}
              className="w-full flex items-center gap-3 rounded-2xl border border-slate-200 hover:border-slate-300 bg-white p-3 text-left transition-colors duration-150"
            >
              {d.thumbnail ? (
                <img src={d.thumbnail} alt="" className="w-14 h-14 rounded-xl object-cover bg-slate-100" />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 text-xs">
                  no img
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900 truncate">{d.name}</div>
                <div className="text-xs text-slate-500 truncate">
                  {d.template.replace(/_/g, ' ')} · {d.panel_count} panels
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => handleDelete(d.id, e)}
                className="text-slate-400 hover:text-red-500 rounded-full"
                data-testid={`btn-delete-${d.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
